import React, { useContext, useMemo } from "react";
import { IOverallDowntimeSummaryPieChartLegendsProps } from "./OverallDowntimeSummaryPieChartLegendsInterfaces";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useTranslation } from "react-i18next";
import { HMIContext } from "../../contexts/HMIContext/HMIContext";
import CircleIcon from "@mui/icons-material/Circle";
import { Typography } from "@mui/material";
import { useStyles } from "./OverallDowntimeSummaryPieChartLegends.styles";
import { localeFormattedNumber } from "utilities/utilities";

export const OverallDowntimeSummaryPieChartLegends = ({
    pieChartData,
    totalEventsInMinutesOrOccurrences,
    countryCode,
}: IOverallDowntimeSummaryPieChartLegendsProps) => {
    const { t } = useTranslation();
    const { displayUnit } = useContext(HMIContext);
    const classes = useStyles();

    const sortedStats = useMemo(() => {
        const stats = [...pieChartData];
        if (displayUnit === "minutes") {
            return stats.sort((a, b) => b.value - a.value);
        } else {
            return stats.sort((a, b) => b.value - a.value);
        }
    }, [displayUnit, pieChartData]);

    return (
        <TableContainer className={classes.tableContainer}>
            <Table size="small" aria-label="a dense table">
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <Typography variant="subtitle2" className={classes.title}>
                                {t("HMI.overall_summary.pie_chart.category").toUpperCase()}
                            </Typography>
                        </TableCell>
                        <TableCell align="right">
                            <Typography variant="subtitle2" className={classes.title}>
                                {displayUnit === "minutes"
                                    ? t("HMI.overall_summary.pie_chart.minutes").toUpperCase()
                                    : t("HMI.overall_summary.pie_chart.occurrences").toUpperCase()}
                            </Typography>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedStats.map((stat) => {
                        if (stat.value) {
                            return (
                                <TableRow className={classes.tableRow} key={stat.id}>
                                    <TableCell
                                        className={`${classes.tableCell} ${classes.tableCellPadding}`}
                                        component="th"
                                        scope="row"
                                    >
                                        <CircleIcon sx={{ color: stat.color }} />
                                        <Typography variant="subtitle1">{stat.name}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="h5">
                                            {localeFormattedNumber(countryCode, stat.value)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            );
                        } else {
                            return null;
                        }
                    })}
                    <TableRow className={classes.tableRow}>
                        <TableCell className={classes.tableCellPadding}>
                            <Typography variant="subtitle2" className={classes.title}>
                                {t("HMI.overall_summary.pie_chart.total").toUpperCase()}
                            </Typography>
                        </TableCell>
                        <TableCell align="right">
                            <Typography variant="h5">
                                {localeFormattedNumber(countryCode, totalEventsInMinutesOrOccurrences)}
                            </Typography>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
};
